'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import type { ContentItem } from '../Level2ContentGrid';
import styles from './Level2CategoryCard.module.scss';

interface Level2CategoryCardProps {
  item: ContentItem;
  index: number;
  onClick?: (item: ContentItem) => void;
  className?: string;
  'data-id'?: string;
}

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      damping: 20,
      stiffness: 100,
    },
  },
};

export function Level2CategoryCard({
  item,
  index,
  onClick,
  className,
  'data-id': dataId
}: Level2CategoryCardProps) {
  
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick(item);
    }
  };

  return (
    <motion.article 
      className={`${styles.contentCard} ${
        item.status ? styles[`status-${item.status}`] : ''
      } ${className || ''}`}
      data-id={dataId}
      variants={itemVariants}
      whileHover={{ 
        y: -5, 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      style={{ 
        '--card-index': index,
      } as React.CSSProperties}
    >
      <Link href={item.href} className={styles.cardLink} onClick={handleClick}>
        {item.image && (
          <div className={styles.cardImage}>
            <img src={item.image} alt="" loading="lazy" />
          </div>
        )}
        <div className={styles.cardContent}>
          {item.status && (
            <span className={`${styles.statusBadge} ${styles[item.status]}`}>
              {item.status === 'new' && '新着'}
              {item.status === 'coming-soon' && '準備中'}
              {item.status === 'active' && 'アクティブ'}
            </span>
          )}
          {item.icon && (
            <div className={styles.cardIcon}>{item.icon}</div>
          )}
          <h2 className={styles.cardTitle}>{item.title}</h2>
          <p className={styles.cardDescription}>{item.description}</p>
          {item.tags && item.tags.length > 0 && (
            <div className={styles.cardTags}>
              {item.tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {item.date && (
            <time className={styles.cardDate} dateTime={item.date}>
              {new Date(item.date).toLocaleDateString('ja-JP')}
            </time>
          )}
          <span className={styles.cardArrow} aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path d="M7 3l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default Level2CategoryCard;